import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { get, uniq } from 'lodash';

import { handleApiError } from '@/utils/api-error-handler';
import { isOktaTokenStorage } from '@/utils/auth';
import { permissionsAppliedToTokenAtom } from '@/utils/permissions-widget';
import {
  getFakeTokenHeader,
  getParsedImpersonatedToken,
  isImpersonating,
} from '@/utils/permissions-widget/token';
import { getUserInfoFromAuth } from '@/utils/user';

import Constants from '@/constants';

import { queryClient } from '@/services/api';
import { ConceptTypeLocations } from '@/services/content-api-types';
import { OktaUserRaw } from '@/types';

import {
  AuthResponse,
  LocationDetails,
  PermittedLocation,
} from '@/_generated/api-clients/xp';
import { store } from '@/atoms';
import { env } from '@/env';
import oktaAuth from '@/okta/oktaConfig';

export const authQueryKey = ['auth'] as const;

const isOktaUserRaw = (claims: unknown): claims is OktaUserRaw => {
  if (typeof claims !== 'object' || claims === null) return false;
  const c = claims as Record<string, unknown>;
  return typeof c.email === 'string' && typeof c.userType === 'string';
};

const _fetchAuth = async (accessToken?: string) => {
  const webToken = oktaAuth.getAccessToken();
  const token =
    accessToken ??
    (window.native.isNative ? window.native.accessToken : webToken);
  if (!token) {
    throw new Error('No token');
  }

  const authState = oktaAuth.authStateManager.getAuthState();

  const impersonatedToken = getParsedImpersonatedToken();

  const parseStorage = (): OktaUserRaw | undefined => {
    try {
      const rawStorage = JSON.parse(
        localStorage.getItem('okta-token-storage') ?? 'null',
      ) as unknown;
      const storage = isOktaTokenStorage(rawStorage) ? rawStorage : undefined;
      const claims = storage?.accessToken?.claims;
      return isOktaUserRaw(claims) ? claims : undefined;
    } catch {
      return undefined;
    }
  };

  const webUser =
    env.REACT_APP_ENV !== 'prod' && impersonatedToken
      ? impersonatedToken
      : Constants.IS_IN_CYPRESS_TEST_LOCAL
        ? parseStorage()
        : (authState?.accessToken?.claims as unknown as
            | OktaUserRaw
            | undefined);

  const user = window.native.isNative ? window.native.nativeUser : webUser;
  if (!user) {
    throw new Error('No okta user found');
  }

  const responsePromise = fetch(`${Constants.XP_API_BASE_URL}/auth`, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...getFakeTokenHeader(),
    },
    credentials: 'include',
  });

  const licenseeStaffPromise = isLicenseeStaff(user)
    ? fetchLocationsForLicenseeStaff(token).catch((e: unknown) => {
        if (isImpersonating()) {
          handleApiError(e, 'fetchAuth');
          return [] as NonNullable<ConceptTypeLocations['locations']>;
        }
        throw e;
      })
    : undefined;

  const [resp, licenseeStaffLocations] = await Promise.all([
    responsePromise,
    licenseeStaffPromise,
  ]);

  if (!resp.ok) {
    throw new Error('Failed to get auth');
  }

  const response = (await resp.json()) as AuthResponse;

  return buildFullAuth(response, user, licenseeStaffLocations);
};

export const fetchAuth = (accessToken?: string) =>
  queryClient.fetchQuery({
    queryKey: authQueryKey,
    queryFn: () => _fetchAuth(accessToken),
    staleTime: 0,
  });

export const getAuth = () => queryClient.getQueryData<Auth>(authQueryKey);

type QueryOptions<TData = unknown, TSelect = unknown> = Omit<
  UseQueryOptions<TData, Error, TSelect>,
  'queryKey' | 'queryFn' | 'initialData'
>;

/** User auth. */
export const useAuth = <TSelect = Auth>(opts?: QueryOptions<Auth, TSelect>) =>
  useQuery({
    ...opts,
    queryKey: authQueryKey,
    queryFn: () => _fetchAuth(),
  });

export const buildFullAuth = (
  auth: AuthResponse,
  user: OktaUserRaw,
  licenseeStaffLocations?: LocationDetails[],
) => {
  const isInternationalUser =
    auth.user?.country === Constants.SUPPORTED_COUNTRIES.GB.id;

  const {
    audience,
    cdn,
    country,
    features,
    isLicensee,
    language,
    locations,
    locationsWithPermissions,
    permissions,
    selectedLocation,
    state,
    userId,
  } = getUserInfoFromAuth(auth);

  const fullName = `${user.nickname} ${user.family_name}`;

  const licenseeLocations =
    licenseeStaffLocations ?? getLocations(auth.user?.locations, 'LIC');

  const franchiseeLocations = getLocations(auth.user?.locations, 'FRANCHISEE');

  const supportCenterLocations = getLocations(auth.user?.locations, 'CORP');

  const corpLocations = supportCenterLocations.filter(
    ({ entity }) => entity !== 'TG',
  );
  const tgLocations = supportCenterLocations
    .filter(({ entity }) => entity === 'TG')
    .map(({ name, operator }) => ({ name, id: operator?.id }));

  const locationsWithLogin = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.LOGIN,
  );

  const highestPermissionLevel = getHighestPermissionLevel(permissions);

  const locationsWithOperatorPermission = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.OPERATOR,
  );
  const locationsWithStakeholderPermission = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.STAKEHOLDER,
  );
  const locationsWithLeaderPermission = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.LEADER,
  );
  const locationsWithTrainerPermission = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.TRAINER,
  );
  const locationsWithLoginPermission = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.LOGIN,
  );
  const locationsWithAdminPermission = getLocationsWithPermissionLevel(
    permissions,
    Constants.USER_PERMISSIONS.ADMIN,
  );

  return {
    audience,
    cdn,
    country,
    features,
    isLicensee,
    language,
    locations,
    locationsWithPermissions,
    permissions,
    selectedLocation,
    state,
    userId,
    isInternationalUser,
    firstName: user.nickname,
    fullName,
    user: auth.user,
    userType: user.userType,
    email: user.email,
    isOperatorConsultant:
      (permissions.OPERATOR?.length ?? 0) > 0 &&
      licenseeLocations.length > 0 &&
      audience === Constants.USER_AUDIENCE.OPERATOR,
    hasSupportCenterLocation: supportCenterLocations.length > 0,
    supportCenterbaseLocations: supportCenterLocations,
    supportCenterLocations: { corpLocations, tgLocations },
    userIsLicenseeStaff: !!licenseeStaffLocations,
    licenseeLocations: licenseeLocations,
    restrictions:
      country.id === Constants.SUPPORTED_COUNTRIES.GB.id
        ? [Constants.USER_RESTRICTIONS.GREAT_BRITAIN_USER]
        : [],
    isStaff: locationsWithLogin.includes(Constants.SUPPORT_STAFF_LOCATION),
    highestPermissionLevel,
    isAdminOperatorOrStakeholder:
      highestPermissionLevel === Constants.USER_PERMISSIONS.ADMIN ||
      highestPermissionLevel === Constants.USER_PERMISSIONS.OPERATOR ||
      highestPermissionLevel === Constants.USER_PERMISSIONS.STAKEHOLDER,
    isStakeholder: locationsWithStakeholderPermission.length > 0,
    leaderCorpLocations: corpLocations.filter(
      corp =>
        corp.number &&
        (locationsWithLeaderPermission.includes(corp.number) ||
          locationsWithStakeholderPermission.includes(corp.number) ||
          locationsWithOperatorPermission.includes(corp.number)),
    ),
    franchiseeLocations,
    locationsWithOperatorPermission,
    locationsWithStakeholderPermission,
    locationsWithLeaderPermission,
    locationsWithTrainerPermission,
    locationsWithLoginPermission,
    locationsWithAdminPermission,
    allLocationsWithAtLeastLeaderPermissions: uniq([
      ...locationsWithLeaderPermission,
      ...locationsWithStakeholderPermission,
      ...locationsWithOperatorPermission,
    ]),
    allLocationsWithAtLeastTrainer: uniq([
      ...locationsWithLeaderPermission,
      ...locationsWithStakeholderPermission,
      ...locationsWithOperatorPermission,
      ...locationsWithTrainerPermission,
    ]),
    assignableTrainerLocations: uniq([
      ...locationsWithLeaderPermission,
      ...locationsWithStakeholderPermission,
      ...locationsWithOperatorPermission,
      ...locationsWithTrainerPermission,
    ]).filter(loc => loc !== Constants.SUPPORT_STAFF_LOCATION),
    allLocationsWithAllPermissions: uniq([
      ...locationsWithLeaderPermission,
      ...locationsWithStakeholderPermission,
      ...locationsWithOperatorPermission,
      ...locationsWithTrainerPermission,
      ...locationsWithLoginPermission,
    ]),
  };
};

export type Auth = Awaited<ReturnType<typeof buildFullAuth>>;

const getLocationsWithPermissionLevel = (
  userPermissions: Record<string, string[]>,
  permissionLevel: string,
): string[] => {
  const permission = Object.keys(userPermissions).find(
    perm => perm === permissionLevel,
  );
  const locations = get(userPermissions, permission ?? '');
  return Array.isArray(locations) && locations.length ? locations : [];
};

const getLocations = (
  locations: PermittedLocation[] | undefined,
  by: LocationDetails['businessModel'],
): LocationDetails[] =>
  (locations ?? []).reduce<LocationDetails[]>((acc, loc) => {
    const location = loc.location;
    if (location && location.businessModel === by) {
      return [...acc, location];
    }

    return acc;
  }, []);

const getHighestPermissionLevel = (permissions: Record<string, string[]>) => {
  if ((permissions.ADMIN?.length ?? 0) > 0) {
    return Constants.USER_PERMISSIONS.ADMIN;
  } else if ((permissions.OPERATOR?.length ?? 0) > 0) {
    return Constants.USER_PERMISSIONS.OPERATOR;
  } else if ((permissions.LEADER?.length ?? 0) > 0) {
    return Constants.USER_PERMISSIONS.LEADER;
  } else if ((permissions.STAKEHOLDER?.length ?? 0) > 0) {
    return Constants.USER_PERMISSIONS.STAKEHOLDER;
  } else if ((permissions.TRAINER?.length ?? 0) > 0) {
    return Constants.USER_PERMISSIONS.TRAINER;
  } else if ((permissions.LOGIN?.length ?? 0) > 0) {
    return Constants.USER_PERMISSIONS.LOGIN;
  }
};

const fetchLocationsForLicenseeStaff = async (accessToken: string) => {
  const resp = await fetch(
    `${Constants.PATHWAY_API.BASE_URL}/locations?type=${Constants.LOCATION_TYPES.LIC}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...getFakeTokenHeader(),
      },
    },
  );

  if (!resp.ok) {
    // Include status info so handleApiError can detect 401/403 errors
    const error = Object.assign(
      new Error('Failed to get locations for licensee staff'),
      {
        status: resp.status,
        statusText: resp.statusText,
        url: resp.url,
      },
    );
    throw error;
  }

  const locations: ConceptTypeLocations[] =
    (await resp.json()) as ConceptTypeLocations[];

  return (
    locations.find(type => type.conceptType === Constants.LOCATION_TYPES.LIC)
      ?.locations ?? []
  );
};

const isLicenseeStaff = (user: OktaUserRaw) => {
  const appliedPerms = store.get(permissionsAppliedToTokenAtom);
  return (
    ((user.cfa_perms.P20.LICENSEESTAFF?.length ?? 0) > 0 ||
      appliedPerms.LICENSEESTAFF) ??
    false
  );
};

export default useAuth;
